/**
 * English: Insights
 *
 * What this file covers: Chat insights: AI reports on a period's chats (what customers asked about, where chats stopped, what went unanswered, the next document to add, and advice on the chatbot, marketing and demand), their filters, downloads and the monthly report.
 *
 * Tone: Clear and encouraging, for business owners reading about their own customers. Say what a figure means in plain words; never promise more than the AI found, and be exact about what a report costs.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const insights: Catalog = {
  'insights.title': 'Insights',
  'insights.description': 'AI reads your chats over a period you choose and reports back: what people asked about, where chats stopped, what went unanswered, what to add to your knowledge base, and ideas for your chatbot and your marketing.',
  'insights.period': '{{from}} to {{to}}',
  'insights.minutes': '{{n}} min',
  'insights.hours': '{{n}} h',

  'insights.new.button': 'New report',
  'insights.new.title': 'New insights report',
  'insights.new.period.label': 'Period',
  'insights.new.period.last': 'Last {{n}} days',
  'insights.new.period.custom': 'Dates',
  'insights.new.period.from': 'From',
  'insights.new.period.to': 'To',
  'insights.new.period.missing': 'Choose both dates.',
  'insights.new.period.order': 'The first date must come before the second.',
  'insights.new.period.future': 'The period cannot end after today.',
  'insights.new.period.long': 'A report covers at most {{max}} days.',
  'insights.new.channels.label': 'Channels',
  'insights.new.channels.hint': 'Leave them all unticked to include every channel.',
  'insights.new.estimate.chats': '{{chats}} chats in this period, {{readable}} with something a customer wrote.',
  'insights.new.estimate.reused': '{{count}} of them were read by an earlier report and have not changed, so they are free.',
  'insights.new.estimate.sampled': 'There are more chats than one report reads, so a fair sample of {{read}} is read (your limit is {{max}}). The counts are worked out from every chat.',
  'insights.new.depth.label': 'How deep it reads',
  'insights.new.focus.label': 'Your questions (optional)',
  'insights.new.focus.hint': 'Up to 5 questions, one per line, each up to 200 characters. The report counts and answers them, for example: How many parents asked about transport?',
  'insights.new.focus.tooMany': 'Ask at most 5 questions.',
  'insights.new.focus.tooLong': 'Keep each question to 200 characters.',
  'insights.new.notice': 'This takes a few minutes (about {{minutes}}). AI reads your chats, and it costs about {{amount}}, taken from your credits as it runs. You can leave this page: we will email you and send a notification when the report is ready.',
  'insights.new.noticeUnpriced': 'an amount we could not work out just now',
  'insights.new.start': 'Start report',
  'insights.new.started': 'Your report has started. We will let you know when it is ready.',

  'insights.depth.recommended': 'Recommended',
  'insights.depth.standard.name': 'Standard',
  'insights.depth.standard.about': 'A quick, low-cost read of every chat, with a careful write-up. Right for most reports.',
  'insights.depth.thorough.name': 'Thorough',
  'insights.depth.thorough.about': 'Every chat read by the stronger model, for sharper detail on what each person wanted.',
  'insights.depth.expert.name': 'Expert',
  'insights.depth.expert.about': 'The strongest model writes the report, for the deepest advice.',
  'insights.depth.models': '{{reader}} reads, {{writer}} writes',
  'insights.depth.price': 'About {{amount}}',
  'insights.depth.unpriced': 'Price not known just now',
  'insights.depth.unavailable': 'Not offered at the moment.',
  'insights.depth.fallback': 'One of its models is switched off, so another stands in for it.',
  'insights.depth.short': 'There is not enough credit for this depth.',

  'insights.running.title': 'Your report is on its way',
  'insights.running.stages': 'Progress',
  'insights.running.queued': 'Waiting to start.',
  'insights.running.gather': 'Counting your chats.',
  'insights.running.reading': 'Reading chats: {{done}} of {{total}}.',
  'insights.running.read': 'Reading your chats.',
  'insights.running.organise': 'Sorting what people asked into topics.',
  'insights.running.write': 'Writing the report.',
  'insights.running.done': 'Finishing.',
  'insights.running.leave': 'You can leave this page. We will email you and send a notification when it is ready.',
  'insights.running.cancel': 'Cancel report',
  'insights.running.cancelTitle': 'Cancel this report?',
  'insights.running.cancelBody': 'It stops now. Only what it has already read is charged.',
  'insights.running.cancelled': 'The report was cancelled.',
  'insights.stage.gather': 'Count chats',
  'insights.stage.read': 'Read chats',
  'insights.stage.organise': 'Sort topics',
  'insights.stage.write': 'Write report',

  'insights.status.queued': 'Waiting',
  'insights.status.running': 'Running',
  'insights.status.done': 'Ready',
  'insights.status.failed': 'Failed',
  'insights.status.cancelled': 'Cancelled',
  'insights.status.skipped': 'Skipped',

  'insights.list.empty': 'No reports yet',
  'insights.list.emptyHint': 'Press New report to see what your customers asked about and how your chatbot did.',
  'insights.list.monthly': 'Monthly report',
  'insights.list.chats': '{{count}} chats',
  'insights.list.answered': '{{pct}}% answered by the chatbot',
  'insights.list.unanswered': '{{count}} left unanswered',
  'insights.list.cost': 'Cost {{amount}}',
  'insights.list.delete': 'Delete report',
  'insights.list.deleteTitle': 'Delete this report?',
  'insights.list.deleteBody': 'The report and its downloads are removed. Your chats are not changed.',
  'insights.list.deleted': 'The report was deleted.',

  'insights.settings.title': 'Monthly report',
  'insights.settings.note': 'A report on the month before, on the 1st of each month. Off until you switch it on.',
  'insights.settings.monthly': 'Make a report every month',
  'insights.settings.monthlyHint': 'It uses the depth, channels and questions below, and is skipped when your credit is short.',
  'insights.settings.next': 'Next monthly report: {{when}}',
  'insights.settings.depth': 'Depth',
  'insights.settings.depthHint': 'How deep the monthly report reads. Each report you start yourself lets you choose again.',
  'insights.settings.max': 'Chats per report',
  'insights.settings.maxHint': '50 to 2 000. Above this, a fair sample is read, which keeps the cost down.',
  'insights.settings.maxRange': 'Choose a number from 50 to 2 000.',
  'insights.settings.saved': 'Saved.',
  'insights.settings.language': 'Language of the report',
  'insights.settings.languageHint': 'The monthly report is written in this language. A report you start yourself is written in the language your console is in.',
  'insights.settings.languageEn': 'English',
  'insights.settings.languageAf': 'Afrikaans',

  'insights.error.credits': 'There is not enough credit for this report. Top up and try again.',
  'insights.error.active': 'A report is already on its way. Wait for it to finish, or cancel it.',
  'insights.error.nothing': 'No chat in this period has anything a customer wrote, so there is nothing to read.',
  'insights.error.notDone': 'This report is not ready yet.',
  'insights.error.forbidden': 'Only the owner or an admin can use Insights.',

  'insights.report.title': 'Insights: {{period}}',
  'insights.report.back': 'All reports',
  'insights.report.read': '{{read}} of {{chats}} chats read',
  'insights.report.readSampled': '≈ {{read}} of {{chats}} chats read (a sample)',
  'insights.report.sampledNote': 'A fair sample of {{read}} of {{chats}} chats was read. Topic counts come from that sample; the other figures count every chat.',
  'insights.report.download': 'Download',
  'insights.report.pdf': 'Download PDF',
  'insights.report.xlsx': 'Download Excel',
  'insights.report.again': 'Run again',
  'insights.report.whole': 'Written by AI for the whole report: it does not change with the filters.',
  'insights.report.depthNote': 'Depth: {{depth}}. Figures are counted; the paragraphs and advice are written by AI from them.',
  'insights.report.missing': 'Report not found',
  'insights.report.missingHint': 'It may have been deleted.',
  'insights.report.loadFailed': 'The report could not be loaded',
  'insights.report.failed': 'This report could not finish. Nothing more was charged. Run it again.',
  'insights.report.cancelled': 'This report was cancelled. Only what it had read was charged.',
  'insights.report.skipped': 'This monthly report was skipped because there was not enough credit.',

  'insights.tabs.overview': 'Overview',
  'insights.tabs.topics': 'Topics',
  'insights.tabs.chatbot': 'Chatbot',
  'insights.tabs.stopped': 'Where chats stopped',
  'insights.tabs.gaps': 'Gaps',
  'insights.tabs.demand': 'Demand and marketing',
  'insights.tabs.improvements': 'Improvements',
  'insights.tabs.chats': 'Chats',
  'insights.tabs.questions': 'Your questions',

  'insights.filter.aria': 'Filters',
  'insights.filter.from': 'From',
  'insights.filter.to': 'To',
  'insights.filter.channel': 'Channel',
  'insights.filter.topic': 'Topic',
  'insights.filter.customerType': 'Customer',
  'insights.filter.outcome': 'Outcome',
  'insights.filter.sentiment': 'Feeling',
  'insights.filter.anyChannel': 'Any channel',
  'insights.filter.anyTopic': 'Any topic',
  'insights.filter.anyCustomerType': 'Anyone',
  'insights.filter.anyOutcome': 'Any outcome',
  'insights.filter.anySentiment': 'Any feeling',
  'insights.filter.showing': 'Showing {{shown}} of {{total}} chats',
  'insights.filter.refs': 'Only the {{count}} chats behind a finding',
  'insights.filter.clear': 'Clear filters',

  'insights.seeChats.one': 'See the chat',
  'insights.seeChats.other': 'See {{count}} chats',
  'insights.empty.filtered': 'No chats match these filters.',
  'insights.empty.topics': 'No topics yet: no chat that matches these filters was read.',

  'insights.kpi.chats': 'Chats',
  'insights.kpi.people': 'People',
  'insights.kpi.newPeople': '{{count}} new',
  'insights.kpi.answeredByBot': 'Resolved by the chatbot',
  'insights.kpi.answeredByBotHint': 'Of {{count}} chats read',
  'insights.kpi.handedToTeam': 'Handed to the team',
  'insights.kpi.unanswered': 'Left unanswered',
  'insights.kpi.unansweredHint': 'The customer had the last word',
  'insights.kpi.firstReply': 'First reply by the team',
  'insights.kpi.firstReplyHint': 'Middle value',
  'insights.kpi.buying': 'Ready to buy or enrol',
  'insights.kpi.buyingHint': '{{count}} strongly',
  'insights.kpi.read': 'Chats read by AI',
  'insights.kpi.readHint': 'With a customer message',
  'insights.kpi.sampled': 'A fair sample',
  'insights.kpi.before': '{{change}} on last period',
  'insights.kpi.filtered': 'These figures are for the chats your filters show, so there is no comparison with the period before.',

  'insights.overview.summary': 'Summary',
  'insights.overview.highlights': 'Highlights',
  'insights.overview.perDay': 'Chats per day',
  'insights.overview.perDayNote': 'Chats with a message that day, and how many of them were people writing for the first time.',
  'insights.overview.chats': 'Chats',
  'insights.overview.newPeople': 'New people',
  'insights.overview.heat': 'When chats start',
  'insights.overview.heatNote': 'By weekday and hour, in your time zone. Darker means more chats; the busiest hours show their number.',
  'insights.overview.heatDay': 'Day',
  'insights.overview.heatCell': '{{day}} {{hour}}:00, {{count}} chats',
  'insights.overview.heatFew': 'Fewer',
  'insights.overview.heatMany': 'More (up to {{count}})',
  'insights.overview.channels': 'Channels',
  'insights.overview.nextSteps': 'Next steps',

  'insights.topics.title': 'What people asked about',
  'insights.topics.note': 'Topics are worked out from your own customers\' words. A chat can be about more than one.',
  'insights.topics.sampled': 'From the sample of chats read. A chat can be about more than one topic.',
  'insights.topics.table': 'Topics',
  'insights.topics.topic': 'Topic',
  'insights.topics.chats': 'Chats',
  'insights.topics.share': 'Share',
  'insights.topics.answered': 'Fully answered',
  'insights.topics.handedOver': 'Handed to the team',
  'insights.topics.trend': 'By day',
  'insights.topics.trendAria': 'Chats about {{name}} by day',
  'insights.topics.byType': 'Topics by customer',
  'insights.topics.byTypeNote': 'How many chats of each kind of customer were about each topic.',

  'insights.answered.yes': 'Answered',
  'insights.answered.partly': 'Partly answered',
  'insights.answered.no': 'Not answered',
  'insights.bot.questions': 'Questions answered',
  'insights.bot.questionsNote': '{{count}} questions customers asked in the chats shown.',
  'insights.bot.split': '{{yes}}% answered, {{partly}}% partly, {{no}}% not answered.',
  'insights.bot.outcomes': 'How chats ended',
  'insights.bot.flows': 'Flows',
  'insights.bot.flowsNote': 'For the whole period: how often each flow started, what started it and how far runs got.',
  'insights.bot.noFlows': 'No flow ran in this period.',
  'insights.bot.flow': 'Flow',
  'insights.bot.starts': 'Started',
  'insights.bot.startedBy': 'Started by',
  'insights.bot.finished': 'Finished',
  'insights.bot.handedOver': 'Handed over',
  'insights.bot.stopped': 'Stopped',
  'insights.bot.funnel': 'How far runs of {{name}} got',
  'insights.bot.reached': 'Runs that reached this step',
  'insights.bot.funnelNote': 'Each step in the order the flow runs it. A big drop between two steps shows where people leave.',
  'insights.bot.ai': 'AI answers',
  'insights.bot.aiNote': 'For the whole period: answers your chatbot wrote from your knowledge base.',
  'insights.bot.aiGiven': 'Answers given',
  'insights.bot.aiSure': 'Sure of the answer',
  'insights.bot.aiNotSure': 'Not sure',
  'insights.bot.notSureQuestions': 'Questions it was not sure about',
  'insights.bot.notSureLine': '{{question}} ({{count}} times)',
  'insights.bot.deep': 'Deep answer review',
  'insights.bot.deepNote': 'The latest deep review in this period, from Flows, Coach.',
  'insights.bot.assessment': 'How the chatbot did',
  'insights.bot.strengths': 'What went well',
  'insights.bot.weaknesses': 'What to fix',
  'insights.start.keyword': 'Keyword',
  'insights.start.intent': 'What they asked',
  'insights.start.greeting': 'Greeting',
  'insights.start.first_contact': 'First message',
  'insights.start.default': 'Default',
  'insights.start.offer': 'Button offered',
  'insights.start.switch': 'Changed flow',
  'insights.start.api': 'API',

  'insights.stopped.title': 'Why chats stopped',
  'insights.stopped.note': 'Chats that ended without what the customer came for, grouped by why.',
  'insights.stopped.none': 'No chat that matches these filters stopped short.',
  'insights.stopped.reason': 'Why',
  'insights.stopped.after': 'Stopped after',
  'insights.stopped.steps': 'Flow steps nobody answered',
  'insights.stopped.stepsNote': 'For the whole period: the step each run stopped at because the customer never replied to its question.',
  'insights.stopped.noSteps': 'No run stopped at a question in this period.',
  'insights.stopped.step': 'Step',
  'insights.stopped.runs': 'Runs',

  'insights.gaps.title': 'Questions your chatbot could not answer',
  'insights.gaps.note': 'Questions not answered or only partly answered, grouped by theme, with your customers\' own words.',
  'insights.gaps.none': 'Every question in these chats was answered.',
  'insights.gaps.theme': 'Theme',
  'insights.gaps.asked': 'Asked',
  'insights.gaps.answer': 'Answer this',
  'insights.gaps.answerNote': 'Answer this adds a correction to your knowledge base. Your chatbot answers from it at once.',
  'insights.gaps.missing': 'What is missing, and why',
  'insights.gaps.missingLine': '{{theme}} ({{count}} times)',
  'insights.gaps.documents': 'Documents to add next',
  'insights.gaps.wouldAnswer': 'Would answer about {{count}} questions',
  'insights.gaps.outline': 'What to put in it',
  'insights.gaps.questionsItAnswers': 'Questions it would answer',
  'insights.gaps.copy': 'Copy outline',
  'insights.gaps.copied': 'Copied',
  'insights.gaps.addDocument': 'Add document',

  'insights.demand.products': 'Products and services asked about',
  'insights.demand.productsNote': 'From the chats read. A chat can name more than one.',
  'insights.demand.noProducts': 'No product or service was named in these chats.',
  'insights.demand.unmet': 'Asked for, but not offered',
  'insights.demand.unmetNote': 'Things customers wanted that you do not offer yet.',
  'insights.demand.marketing': 'Marketing ideas',
  'insights.demand.audience': 'Who: {{value}}',
  'insights.demand.channel': 'Where: {{value}}',
  'insights.demand.timing': 'When: {{value}}',
  'insights.demand.opportunities': 'Opportunities',
  'insights.demand.risks': 'Risks',
  'insights.demand.complaints': 'Complaints',
  'insights.demand.complaintsNote': '{{count}} chats with a complaint.',
  'insights.demand.leads': 'Ready to buy or enrol',
  'insights.demand.leadsNote': 'People who showed they want to buy, book or enrol. Strong signals first.',
  'insights.demand.noLeads': 'Nobody in these chats showed they were ready to buy.',
  'insights.demand.signal': 'Signal',
  'insights.demand.wants': 'Wants',
  'insights.demand.tag': 'Tag these people',
  'insights.demand.tagTitle': 'Tag these people in your contacts',
  'insights.demand.tagIntro': '{{count}} of them are in your contacts. Give them a tag, then send them a broadcast by that tag.',
  'insights.demand.tagWithout': '{{count}} cannot be tagged, because they wrote on your website and are not in your contacts.',
  'insights.demand.tagName': 'Tag',
  'insights.demand.tagHint': 'Up to 64 characters, for example: Enrolment 2027 leads.',
  'insights.demand.tagSave': 'Tag {{count}} contacts',
  'insights.demand.tagged': '{{count}} contacts tagged {{tag}}.',

  'insights.improve.title': 'Improvements',
  'insights.improve.none': 'No improvements were suggested in this report.',
  'insights.improve.allAreas': 'All',
  'insights.improve.what': 'What to do',
  'insights.improve.area': 'Area',
  'insights.improve.impact': 'Impact',
  'insights.improve.effort': 'Effort',
  'insights.improve.evidence': 'Why: {{value}}',
  'insights.area.chatbot': 'Chatbot',
  'insights.area.flows': 'Flows',
  'insights.area.knowledge': 'Knowledge base',
  'insights.area.team': 'Team',
  'insights.area.hours': 'Hours',
  'insights.area.other': 'Other',
  'insights.level.high': 'High',
  'insights.level.medium': 'Medium',
  'insights.level.low': 'Low',

  'insights.chats.title': 'Chats',
  'insights.chats.note': '{{count}} chats, newest first. Open one to read it in the inbox.',
  'insights.chats.csv': 'Download CSV',
  'insights.chats.ref': 'Ref',
  'insights.chats.date': 'Date',
  'insights.chats.person': 'Person',
  'insights.chats.channel': 'Channel',
  'insights.chats.summary': 'What it was about',
  'insights.chats.outcome': 'Outcome',
  'insights.chats.open': 'Open chat',
  'insights.chats.unknown': 'Unknown',
  'insights.chats.flows': 'Flows: {{names}}',
  'insights.chats.notRead': 'Not read by AI (no customer message, or left out of the sample).',

  'insights.questions.none': 'This report has no questions of your own.',
  'insights.questions.noAnswer': 'There was no answer to this question.',
  'insights.questions.counts': 'In the chats shown: {{yes}} yes, {{no}} no, {{unclear}} unclear.',

  'insights.outcome.resolved_by_bot': 'Resolved by the chatbot',
  'insights.outcome.resolved_by_team': 'Resolved by the team',
  'insights.outcome.waiting_for_team': 'Waiting for the team',
  'insights.outcome.abandoned': 'Customer left',
  'insights.outcome.info_only': 'Information only',
  'insights.outcome.spam': 'Spam',
  'insights.sentiment.positive': 'Positive',
  'insights.sentiment.neutral': 'Neutral',
  'insights.sentiment.negative': 'Negative',
  'insights.buying.none': 'None',
  'insights.buying.some': 'Some interest',
  'insights.buying.strong': 'Strong',
  'insights.channel.whatsapp': 'WhatsApp',
  'insights.channel.web': 'Website chat',
  'insights.channel.messenger': 'Messenger',
  'insights.channel.instagram': 'Instagram',
  'insights.channel.email': 'Email',
  'insights.channel.sms': 'SMS',
  'insights.channel.voice': 'Phone call',

  'insights.csv.ref': 'Ref',
  'insights.csv.date': 'Date',
  'insights.csv.channel': 'Channel',
  'insights.csv.person': 'Person',
  'insights.csv.returning': 'Wrote before',
  'insights.csv.topics': 'Topics',
  'insights.csv.products': 'Products and services',
  'insights.csv.customerType': 'Customer',
  'insights.csv.outcome': 'Outcome',
  'insights.csv.sentiment': 'Feeling',
  'insights.csv.stopReason': 'Why it stopped',
  'insights.csv.stoppedAfter': 'Stopped after',
  'insights.csv.buying': 'Ready to buy',
  'insights.csv.buyingDetail': 'Wants',
  'insights.csv.unanswered': 'Left unanswered',
  'insights.csv.takenOver': 'Handed to the team',
  'insights.csv.firstReply': 'First reply (minutes)',
  'insights.csv.flows': 'Flows',
  'insights.csv.summary': 'Summary',
  'insights.csv.quote': 'Customer\'s words',
  'insights.csv.yes': 'Yes',
  'insights.csv.no': 'No',
  'insights.stage.done': 'Done',
  'insights.stage.now': 'In progress',
  'insights.stage.later': 'Waiting',
  'insights.stage.of': '{{done}} of {{total}}',
  'insights.running.overall': '{{pct}}% done',
  'insights.gaps.suggestFlows': 'Suggest flows for these'
};

export default insights;
